import React from "react";
import { Box, Card, Typography, useTheme } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import type { MetricStatValue } from "../../../shared/api/types";
import { formatTrendPercent, getRagColor } from "../../../shared/utils";

interface StatCardProps {
  metric: MetricStatValue;
  icon: React.ComponentType<SvgIconProps>;
}

export const StatCard: React.FC<StatCardProps> = ({
  metric,
  icon: IconComponent,
}) => {
  const theme = useTheme();
  const normalizedLabel = metric.label.toLowerCase().trim();
  const isRestingHrMetric =
    normalizedLabel.includes("resting hr") ||
    normalizedLabel.includes("resting heart rate");
  const isRespiratoryRateMetric =
    normalizedLabel.includes("respiratory rate");
  const isRespiratoryInHealthyRange =
    typeof metric.value === "number" && metric.value >= 12 && metric.value <= 20;

  const color = getRagColor(metric.rag_color, theme);
  const normalizedChange = formatTrendPercent(metric.change) ?? "";
  const isNegativeChange = normalizedChange.trim().startsWith("-");
  const isPositiveChange = normalizedChange.trim().startsWith("+");
  const trendColor = isRespiratoryRateMetric
    ? isRespiratoryInHealthyRange
      ? theme.palette.success.main
      : theme.palette.error.main
    : isRestingHrMetric
      ? isNegativeChange
        ? theme.palette.success.main
        : isPositiveChange
          ? theme.palette.error.main
          : theme.palette.text.secondary
      : isNegativeChange
        ? theme.palette.error.main
        : isPositiveChange
          ? theme.palette.success.main
          : theme.palette.text.secondary;
  const TrendIcon = isNegativeChange
    ? TrendingDownIcon
    : isPositiveChange
      ? TrendingUpIcon
      : TrendingFlatIcon;

  return (
    <Card
      sx={{ border: "1px solid", borderColor: "divider", boxShadow: "none" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <IconComponent fontSize="large" sx={{ color }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            {metric.label}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 0.75,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
              <Typography
                variant="h5"
                component="span"
                sx={{
                  fontWeight: 700,
                }}
              >
                {Math.round(metric.value * 10) / 10}
              </Typography>
              {metric.unit && (
                <Typography variant="caption" color="text.secondary">
                  {metric.unit}
                </Typography>
              )}
            </Box>
            {metric.change && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.25,
                  color: trendColor,
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                  }}
                >
                  {normalizedChange}
                </Typography>
                <TrendIcon
                  sx={{
                    fontSize: "0.875rem",
                    color: trendColor,
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Card>
  );
};
